import { useCustom } from "@refinedev/core";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Typography,
  Button,
  LinearProgress,
  Box,
  Container,
  Card,
} from "@mui/material";
import { CarrierServicesApiResponse, CarrierService } from "./types";
import { useMemo, useState } from "react";
import { ShipmentFilters } from "./ShipmentFilters";
import { useTranslation } from "../../../hooks/useTranslation";
import { NavLink } from "react-router";

export const ShipmentPrices = () => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.shipmentPrices"
  );

  const { data, isLoading } = useCustom<CarrierServicesApiResponse>({
    url: `carrier-services`,
    method: "get",
  });

  const [selectedCountry, setSelectedCountry] = useState<number | undefined>();
  const [selectedCarrier, setSelectedCarrier] = useState<number | undefined>();

  const carrierServices = data?.data?.carrierServices ?? [];

  const availableCarriers = useMemo(() => {
    if (!selectedCountry) return [];
    const carriers = carrierServices
      .filter((s) => s.countryId === selectedCountry)
      .map((s) => ({
        carrierId: s.carrierId,
        carrierName: s.carrierName,
      }));
    return Array.from(new Map(carriers.map((c) => [c.carrierId, c])).values());
  }, [carrierServices, selectedCountry]);

  const filteredServices = useMemo(() => {
    let filtered = carrierServices;
    if (selectedCountry) {
      filtered = filtered.filter((s) => s.countryId === selectedCountry);
    }
    if (selectedCarrier) {
      filtered = filtered.filter((s) => s.carrierId === selectedCarrier);
    }
    return filtered;
  }, [carrierServices, selectedCountry, selectedCarrier]);

  const availableCountries = useMemo(() => {
    const countries = carrierServices.map((s) => ({
      countryId: s.countryId,
      countryName: s.countryName,
      countryFlagUrl: s.countryFlagUrl,
    }));
    return Array.from(new Map(countries.map((c) => [c.countryId, c])).values());
  }, [carrierServices]);

  const handleSelectedCountryChange = (countryId?: number) => {
    setSelectedCountry(countryId);
    setSelectedCarrier(undefined);
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <Container component={Card} sx={{ padding: 2 }} maxWidth="xl">
      <Typography variant="h5" sx={{ marginBottom: 2 }}>
        {translate("title")}
      </Typography>
      <ShipmentFilters
        countries={availableCountries}
        carriers={availableCarriers}
        selectedCountry={selectedCountry}
        selectedCarrier={selectedCarrier}
        onCountryChange={handleSelectedCountryChange}
        onCarrierChange={setSelectedCarrier}
      />

      <TableContainer
        sx={{
          borderRadius: 1,
          overflowY: "auto",
          maxHeight: "calc(100vh - 300px)",
        }}
      >
        <Table
          sx={{
            border: (theme) =>
              `1px solid ${theme.palette.customBackground.box}`,
            paddingTop: 2,
          }}
        >
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.customBackground.box,
              position: "sticky",
              top: "-1px",
              zIndex: 1,
            }}
          >
            <TableRow>
              <TableCell>{translate("fields.country")}</TableCell>
              <TableCell>{translate("fields.carrier")}</TableCell>
              <TableCell>{translate("fields.service")}</TableCell>
              <TableCell>{translate("fields.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredServices.map((service: CarrierService) => (
              <TableRow
                key={`${service.id}-${service.countryId}-${service.carrierId}`}
              >
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      src={service.countryFlagUrl + "?width=240"}
                      alt={service.countryName}
                      sx={{ width: 24, height: 24 }}
                      variant="square"
                      slotProps={{
                        img: { style: { objectFit: "contain" } },
                      }}
                    />
                    <Typography variant="subtitle1">
                      {service.countryName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      src={service.carrierLogoUrl + "?width=240"}
                      alt={service.carrierName}
                      sx={{ width: 32 }}
                      variant="square"
                      slotProps={{
                        img: { style: { objectFit: "contain" } },
                      }}
                    />
                    <Typography variant="subtitle2">
                      {service.carrierName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{service.name}</Typography>
                </TableCell>
                <TableCell>
                  <NavLink
                    to={`edit/${service.countryId}/${service.carrierId}/${service.id}`}
                  >
                    <Button variant="outlined" size="small">
                      {translateWithoutPrefix("buttons.edit")}
                    </Button>
                  </NavLink>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};
